<script lang="ts">
  // ============================================================
  // Sparkline SVG inline para la equity curve de la session.
  // - Eje X = trade index (uniforme, no temporal).
  // - Eje Y = cumulativePnL escalado al rango [paddingY, height-paddingY].
  // - Zero-line dashed.
  // - Dots en cada punto (visibles cuando hay pocos trades).
  // - Color: verde si final >=0, rojo si <0.
  //
  // Mitigacion del riesgo Fase 4 (N plotlys pega con muchas sessions):
  // SVG inline = 0 dependencias, render trivial.
  // ============================================================

  interface Props {
    points: Array<{ exitTime: string; cumulativePnL: number }>;
    width?: number;
    height?: number;
    showValue?: boolean;
    class?: string;
  }

  let {
    points,
    width = 140,
    height = 44,
    showValue = true,
    class: cls = '',
  }: Props = $props();

  let pathInfo = $derived.by(() => {
    if (points.length === 0) return null;

    const ys = points.map((p) => p.cumulativePnL);
    // Incluir 0 en el rango para que el zero-line siempre sea visible.
    const minY = Math.min(...ys, 0);
    const maxY = Math.max(...ys, 0);
    const range = maxY - minY || 1;

    const padX = 4;
    const padY = 4;
    const usableW = width - padX * 2;
    const usableH = height - padY * 2;

    // Single point: dibujamos solo el dot (sin path).
    const stepX = points.length > 1 ? usableW / (points.length - 1) : 0;

    const coords = points.map((p, i) => {
      const x = padX + i * stepX;
      const y = padY + (1 - (p.cumulativePnL - minY) / range) * usableH;
      return { x, y, pnl: p.cumulativePnL };
    });

    const path = coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
      .join(' ');

    const last = ys[ys.length - 1] ?? 0;
    const isPositive = last >= 0;
    const zeroY = padY + (1 - (0 - minY) / range) * usableH;

    // Area de relleno suave bajo la curva (hasta zero-line).
    const areaPath =
      coords.length > 1
        ? `${path} L${coords[coords.length - 1]!.x.toFixed(2)},${zeroY.toFixed(2)} L${coords[0]!.x.toFixed(2)},${zeroY.toFixed(2)} Z`
        : null;

    return {
      coords,
      path,
      areaPath,
      isPositive,
      zeroY,
      last,
    };
  });

  function fmtPnL(n: number): string {
    if (n === 0) return '$0';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(0)}`;
  }
</script>

{#if pathInfo}
  <div class={['inline-flex items-center gap-2', cls].join(' ')}>
    <svg
      {width}
      {height}
      viewBox={`0 0 ${width} ${height}`}
      class="overflow-visible"
      aria-hidden="true"
    >
      <!-- Zero line -->
      <line
        x1="0"
        x2={width}
        y1={pathInfo.zeroY}
        y2={pathInfo.zeroY}
        stroke="currentColor"
        stroke-width="0.5"
        stroke-dasharray="2 2"
        class="text-muted-foreground/40"
      />

      <!-- Area fill (tinted) -->
      {#if pathInfo.areaPath}
        <path
          d={pathInfo.areaPath}
          fill="currentColor"
          fill-opacity="0.15"
          class={pathInfo.isPositive ? 'text-success' : 'text-destructive'}
        />
      {/if}

      <!-- Curve -->
      <path
        d={pathInfo.path}
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class={pathInfo.isPositive ? 'text-success' : 'text-destructive'}
      />

      <!-- Dots por trade -->
      {#each pathInfo.coords as c, i (i)}
        <circle
          cx={c.x}
          cy={c.y}
          r="2"
          fill="currentColor"
          class={c.pnl >= 0 ? 'text-success' : 'text-destructive'}
        />
      {/each}
    </svg>

    {#if showValue}
      <!-- Label del ultimo valor -->
      <span
        class={[
          'font-mono text-xs font-semibold tabular-nums',
          pathInfo.isPositive ? 'text-success' : 'text-destructive',
        ].join(' ')}
      >
        {fmtPnL(pathInfo.last)}
      </span>
    {/if}
  </div>
{:else}
  <span class="text-muted-foreground/40 text-xs">—</span>
{/if}
